import React, { useEffect, useRef, ReactNode } from 'react';
import { useChatContext } from './store/ChatContext';
import MessageBubble from './MessageBubble';
import ToolStatusBar from './ToolStatusBar';
import { UIMessage } from './types/chat.types';
import { Spin } from 'antd';

interface Props {
  avatarEmoji?: string;
  emptyStateContent?: ReactNode;
  blockedMessage?: string;
  isBlocked?: boolean;
  extraBottom?: ReactNode;  // e.g. ItinerarySummaryCard slot
  renderMessageExtra?: (message: UIMessage) => ReactNode;
  /** Passed through to every MessageBubble; see MessageBubble.Props.onLinkClick. */
  onLinkClick?: (url: string, e: React.MouseEvent) => boolean;
}

const ChatMessages: React.FC<Props> = ({
  avatarEmoji = '🤖',
  emptyStateContent,
  blockedMessage = 'This chat has been disabled.',
  isBlocked = false,
  extraBottom,
  renderMessageExtra,
  onLinkClick,
}) => {
  const { state } = useChatContext();
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.messages, state.streamingText, state.activeToolLabel]);

  if (state.isLoadingHistory) {
    return <div className="bot-messages bot-messages--loading"><Spin /></div>;
  }

  const streamingMsg: UIMessage | null = state.isStreaming
    ? { id: '__streaming__', role: 'assistant', text: state.streamingText, timestamp: new Date().toISOString(), isStreaming: true }
    : null;

  const showReconnectBanner = state.socketStatus === 'disconnected' || state.socketStatus === 'error';

  return (
    <div className="bot-messages">
      {showReconnectBanner && (
        <div className={`bot-reconnect-banner bot-reconnect-banner--${state.socketStatus}`}>
          {state.socketStatus === 'error' ? 'Connection error — retrying…' : 'Reconnecting…'}
        </div>
      )}
      {state.messages.length === 0 && !state.isStreaming && emptyStateContent}
      {state.messages.map(msg => (
        <React.Fragment key={msg.id}>
          <MessageBubble message={msg} avatarEmoji={avatarEmoji} onLinkClick={onLinkClick} />
          {renderMessageExtra?.(msg)}
        </React.Fragment>
      ))}
      {streamingMsg && <MessageBubble message={streamingMsg} avatarEmoji={avatarEmoji} />}
      <ToolStatusBar />
      {isBlocked && <div className="bot-blocked-banner">{blockedMessage}</div>}
      {extraBottom}
      <div ref={bottomRef} />
    </div>
  );
};

export default ChatMessages;
